const MIN_WIDTH = 50;

export default class ImageResizeView {
  constructor(node, view, getPos, options = {}) {
    this.node = node;
    this.view = view;
    this.getPos = getPos;
    this.useStyleWidth = options.useStyleWidth || false;
    this.onResizeEnd = options.onResizeEnd || null;
    this.aspectRatio = null;
    this.dragging = false;
    this.overlay = null;
    this.sizeLabel = null;

    this.dom = document.createElement('span');
    this.dom.classList.add('image-resize-wrapper');

    this.img = document.createElement('img');
    this.syncImgAttrs(node);
    this.dom.appendChild(this.img);

    this.handle = document.createElement('div');
    this.handle.classList.add('image-resize-handle');

    if (this.img.naturalWidth) {
      this.aspectRatio = this.img.naturalWidth / this.img.naturalHeight;
    } else {
      this.img.addEventListener(
        'load',
        () => {
          this.aspectRatio =
            this.img.naturalWidth / this.img.naturalHeight || 1;
        },
        { once: true }
      );
    }

    this.boundMouseDown = this.onMouseDown.bind(this);
    this.boundMouseMove = this.onMouseMove.bind(this);
    this.boundMouseUp = this.onMouseUp.bind(this);
    this.boundResize = () => {
      if (this.overlay) this.refreshOverlay();
    };
    this.handle.addEventListener('mousedown', this.boundMouseDown);
    window.addEventListener('resize', this.boundResize);
  }

  syncImgAttrs(node) {
    const { src, alt, title, width, height, style } = node.attrs;
    this.img.setAttribute('src', src || '');
    if (alt) this.img.setAttribute('alt', alt);
    else this.img.removeAttribute('alt');
    if (title) this.img.setAttribute('title', title);
    else this.img.removeAttribute('title');
    if (width) this.img.setAttribute('width', width);
    else this.img.removeAttribute('width');
    if (height) this.img.setAttribute('height', height);
    else this.img.removeAttribute('height');
    if (style) this.img.setAttribute('style', style);
    else this.img.removeAttribute('style');
  }

  showOverlay() {
    if (this.overlay) return;

    const rect = this.img.getBoundingClientRect();
    const wrapperRect = this.dom.getBoundingClientRect();

    this.overlay = document.createElement('div');
    this.overlay.classList.add('image-resize-overlay');
    this.overlay.style.width = `${rect.width}px`;
    this.overlay.style.height = `${rect.height}px`;
    this.overlay.style.left = `${rect.left - wrapperRect.left}px`;
    this.overlay.style.top = `${rect.top - wrapperRect.top}px`;

    this.sizeLabel = document.createElement('div');
    this.sizeLabel.classList.add('image-resize-size-label');
    const dims = this.getConfiguredDimensions();
    this.updateSizeLabel(dims.width, dims.height);
    this.overlay.appendChild(this.sizeLabel);

    this.overlay.appendChild(this.handle);
    this.dom.appendChild(this.overlay);
  }

  hideOverlay() {
    if (this.overlay) {
      this.overlay.remove();
      this.overlay = null;
      this.sizeLabel = null;
    }
  }

  updateSizeLabel(width, height) {
    if (this.sizeLabel) {
      this.sizeLabel.textContent = `${width} × ${height}`;
    }
  }

  getConfiguredDimensions() {
    const { width, height, style } = this.node.attrs;
    let w = parseInt(width, 10) || null;
    let h = parseInt(height, 10) || null;

    if (!w && style) {
      const match = style.match(/max-width:\s*(\d+)/);
      if (match) w = parseInt(match[1], 10);
    }

    if (!w && !h) {
      return {
        width:
          this.img.naturalWidth ||
          Math.round(this.img.getBoundingClientRect().width),
        height:
          this.img.naturalHeight ||
          Math.round(this.img.getBoundingClientRect().height),
      };
    }

    if (w && !h && this.aspectRatio) h = Math.round(w / this.aspectRatio);
    if (h && !w && this.aspectRatio) w = Math.round(h * this.aspectRatio);

    return { width: w, height: h };
  }

  refreshOverlay() {
    if (!this.overlay) return;
    const rect = this.img.getBoundingClientRect();
    const wrapperRect = this.dom.getBoundingClientRect();
    this.overlay.style.width = `${rect.width}px`;
    this.overlay.style.height = `${rect.height}px`;
    this.overlay.style.left = `${rect.left - wrapperRect.left}px`;
    this.overlay.style.top = `${rect.top - wrapperRect.top}px`;
    const dims = this.getConfiguredDimensions();
    this.updateSizeLabel(dims.width, dims.height);
  }

  onMouseDown(e) {
    e.preventDefault();
    e.stopPropagation();
    this.dragging = true;
    this.startX = e.clientX;
    this.startWidth = this.img.getBoundingClientRect().width;
    this.startHeight = this.img.getBoundingClientRect().height;

    if (!this.aspectRatio && this.img.naturalWidth) {
      this.aspectRatio = this.img.naturalWidth / this.img.naturalHeight || 1;
    }

    const editorEl = this.view.dom.closest('.editor--root');
    if (editorEl) editorEl.classList.add('prosemirror-image-resizing');

    document.addEventListener('mousemove', this.boundMouseMove);
    document.addEventListener('mouseup', this.boundMouseUp);
  }

  onMouseMove(e) {
    if (!this.dragging || !this.overlay) return;
    const dx = e.clientX - this.startX;
    const maxWidth = Math.min(
      this.view.dom.getBoundingClientRect().width,
      this.img.naturalWidth || Infinity
    );
    const newWidth = Math.max(
      MIN_WIDTH,
      Math.min(this.startWidth + dx, maxWidth)
    );

    const newHeight = this.aspectRatio
      ? Math.round(newWidth / this.aspectRatio)
      : Math.round(newWidth);
    this.overlay.style.width = `${newWidth}px`;
    this.overlay.style.height = `${newHeight}px`;
    this.updateSizeLabel(Math.round(newWidth), newHeight);
  }

  onMouseUp() {
    if (!this.dragging) return;
    this.dragging = false;

    document.removeEventListener('mousemove', this.boundMouseMove);
    document.removeEventListener('mouseup', this.boundMouseUp);

    const editorEl = this.view.dom.closest('.editor--root');
    if (editorEl) editorEl.classList.remove('prosemirror-image-resizing');

    const finalWidth = this.overlay
      ? parseFloat(this.overlay.style.width)
      : this.startWidth;
    const finalHeight = this.overlay
      ? parseFloat(this.overlay.style.height)
      : this.startHeight;

    const pos = this.getPos();
    if (pos == null) return;

    const isOriginalSize =
      this.img.naturalWidth && Math.round(finalWidth) >= this.img.naturalWidth;

    const attrs = { ...this.node.attrs };
    if (isOriginalSize) {
      attrs.style = null;
      attrs.height = null;
      attrs.width = null;
    } else if (this.useStyleWidth) {
      attrs.style = `max-width: ${Math.round(finalWidth)}px`;
    } else {
      attrs.height = `${Math.round(finalHeight)}px`;
    }

    const tr = this.view.state.tr.setNodeMarkup(pos, null, attrs);
    this.view.dispatch(tr);

    // Refresh overlay to match new image size
    requestAnimationFrame(() => this.refreshOverlay());

    if (this.onResizeEnd) this.onResizeEnd();
  }

  update(node) {
    if (node.type !== this.node.type) return false;
    this.node = node;
    this.syncImgAttrs(node);
    // Refresh overlay after preset size change
    if (this.overlay) {
      requestAnimationFrame(() => this.refreshOverlay());
    }
    return true;
  }

  selectNode() {
    this.dom.classList.add('ProseMirror-selectednode');
    this.img.classList.add('ProseMirror-selectednode');
    this.showOverlay();
  }

  deselectNode() {
    this.dom.classList.remove('ProseMirror-selectednode');
    this.img.classList.remove('ProseMirror-selectednode');
    this.hideOverlay();
  }

  stopEvent(event) {
    if (this.handle.contains(event.target)) return true;
    if (this.dragging) return true;
    return false;
  }

  // eslint-disable-next-line class-methods-use-this
  ignoreMutation() {
    return true;
  }

  destroy() {
    this.hideOverlay();
    this.handle.removeEventListener('mousedown', this.boundMouseDown);
    document.removeEventListener('mousemove', this.boundMouseMove);
    document.removeEventListener('mouseup', this.boundMouseUp);
    window.removeEventListener('resize', this.boundResize);
  }
}
